import { Card } from '@tremor/react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { withTranslation } from 'react-i18next';
import BarChart from './BarChart';
import LineChart from './LineChart';
import AreaChart from './AreaChart';
import DonutChart from './DonutChart';
import BarList from './BarList';

/**
 * Translate a metric key using the insights_metrics namespace.
 * Falls back to the raw key if no translation is found.
 */
function translateMetricKey(t, key) {
  return t(`insights_metrics.${key}`, { defaultValue: key });
}

/**
 * Renames valueKeys in both the categories array and each data row.
 * This is necessary because Tremor uses the field name as both the data
 * accessor AND the legend label.
 */
function withTranslatedValueKeys(data, valueKeys, t) {
  if (!valueKeys || valueKeys.length === 0) return { tData: data, tKeys: valueKeys };

  const keyMap = {};
  valueKeys.forEach(k => {
    keyMap[k] = translateMetricKey(t, k);
  });

  const tKeys = valueKeys.map(k => keyMap[k]);
  const tData = data.map(row => {
    const newRow = {};
    Object.keys(row).forEach(k => {
      newRow[k in keyMap ? keyMap[k] : k] = row[k];
    });
    return newRow;
  });

  return { tData, tKeys };
}

/**
 * Universal chart renderer.
 *
 * Props:
 *   chart   - full chart object from the API:
 *             { chart_type, config, data, title, description, grid_cols }
 *   loading - optional boolean to show a spinner instead of the chart
 *   t       - translation function (injected by withTranslation HOC)
 */
function InsightsChart(props) {
  const { chart, loading = false, t } = props;

  if (loading) {
    return (
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Card>
    );
  }

  if (!chart) return null;

  const {
    title,
    description,
    chart_type,
    config = {},
    data = [],
  } = chart;

  const {
    index_key,
    value_keys = [],
    colors,
    x_label,
    y_label,
    category_key,
    value_key,
    tremor: tremorConfig = {},
  } = config;

  const { tData, tKeys } = withTranslatedValueKeys(data, value_keys, t);

  const renderChart = () => {
    switch (chart_type) {
      case 'bar':
        return (
          <BarChart
            data={tData}
            indexKey={index_key}
            valueKeys={tKeys}
            colors={colors}
            xLabel={x_label}
            yLabel={y_label}
            tremorConfig={tremorConfig}
          />
        );

      case 'line':
        return (
          <LineChart
            data={tData}
            indexKey={index_key}
            valueKeys={tKeys}
            colors={colors}
            xLabel={x_label}
            yLabel={y_label}
            tremorConfig={tremorConfig}
          />
        );

      case 'area':
        return (
          <AreaChart
            data={tData}
            indexKey={index_key}
            valueKeys={tKeys}
            colors={colors}
            xLabel={x_label}
            yLabel={y_label}
            tremorConfig={tremorConfig}
          />
        );

      case 'donut':
        return (
          <DonutChart
            data={data}
            categoryKey={category_key}
            valueKey={value_key}
            colors={colors}
            tremorConfig={tremorConfig}
          />
        );

      case 'bar_list':
        return (
          <BarList
            data={data}
            nameKey={category_key || 'name'}
            valueKey={value_key || 'value'}
            tremorConfig={tremorConfig}
          />
        );

      case 'scatter':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Scatter chart coming soon
            </Typography>
          </Box>
        );

      default:
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Chart type "{chart_type}" is not supported yet.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Card>
      {title && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {renderChart()}
    </Card>
  );
}

export default withTranslation()(InsightsChart);
